// Client for the per-site identified-visitors list. Shape mirrors
// packages/server/src/routes/identified-visitors.ts.

import { apiFetch } from './api';

/** One way a person reached the site (a visitor linked to their contact). */
export interface ContactIdentity {
  visitorId: string;
  contactId: string;
  channel: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  lastSeenAt: string | null;
}

/** A person in the Contacts list. Visitors linked to one contact across
 *  channels are folded into one row, keyed by the most recently seen. */
export interface IdentifiedVisitor {
  visitorId: string;
  /** null for a legacy visitor not yet linked to a contact. */
  contactId: string | null;
  /** Every channel they have used ('web', 'whatsapp', 'api'). */
  channels: string[];
  /** All their linked identities, most recently seen first. */
  identities: ContactIdentity[];
  name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  isReturning: boolean;
  lastPagePath: string | null;
  identifiedAt: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  /** Latest session for this visitor — null if they have none. */
  lastSessionId: string | null;
}

export interface IdentifiedVisitorsResult {
  visitors: IdentifiedVisitor[];
  capped: boolean;
  /** 'all' = full site list (admin); 'mine' = only this agent's handled chats. */
  scope: 'all' | 'mine';
}

export async function listIdentifiedVisitors(siteId: string): Promise<IdentifiedVisitorsResult> {
  return apiFetch<IdentifiedVisitorsResult>(`/api/admin/sites/${siteId}/identified-visitors`);
}

/** One chat belonging to a contact. Mirrors GET /api/visitors/:id/sessions. */
export interface ContactSession {
  sessionId: string;
  /** The visitor row the chat belongs to. A contact reached on several
   *  channels has one visitor per channel, so this can differ from the row
   *  the list was opened from. */
  visitorId?: string;
  medium: 'chat' | 'video';
  channel?: 'web' | 'api' | 'whatsapp' | 'email' | 'instagram' | 'messenger';
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  starred: boolean;
  /** Set when the chat was soft-deleted from the conversation view. */
  deletedAt: string | null;
  agentName: string | null;
  messageCount: number;
}

/** Every chat this contact has had, newest first (soft-deleted ones included). */
export async function listContactSessions(visitorId: string): Promise<ContactSession[]> {
  const r = await apiFetch<{ sessions: ContactSession[] }>(
    `/api/visitors/${encodeURIComponent(visitorId)}/sessions`,
  );
  return r.sessions;
}

export interface PurgeCounts {
  sessions: number;
  messages: number;
  recordings: number;
}

/**
 * Hard-delete one of the contact's chats — messages, attachments, notes,
 * transcripts and recordings go with it. Not the same as the conversation
 * view's delete, which only hides the chat. Server enforces admin rights.
 */
export async function deleteContactSession(
  visitorId: string,
  sessionId: string,
): Promise<PurgeCounts> {
  return apiFetch<PurgeCounts>(
    `/api/visitors/${encodeURIComponent(visitorId)}/sessions/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE' },
  );
}

/** Hard-delete the contact and every chat they had. Irreversible. */
export async function deleteContact(visitorId: string): Promise<PurgeCounts> {
  return apiFetch<PurgeCounts>(`/api/visitors/${encodeURIComponent(visitorId)}`, {
    method: 'DELETE',
  });
}

// ─── Activity timeline ───
// Mirrors packages/server/src/routes/contact-timeline.ts.

export type TimelineKind =
  | 'page_view'
  | 'chat_started'
  | 'chat_ended'
  | 'ticket_opened'
  | 'ticket_resolved'
  | 'ticket_closed'
  | 'identified'
  | 'channel_linked';

export interface TimelineEvent {
  id: string;
  kind: TimelineKind;
  at: number;
  url?: string;
  sessionId?: string;
  channel?: string;
  medium?: string;
  agentName?: string | null;
  endReason?: string | null;
  ticketId?: string;
  ticketNumber?: number;
  subject?: string;
}

export interface TimelinePage {
  events: TimelineEvent[];
  /** Pass back as `before` for older events; null = the start of the history. */
  nextBefore: number | null;
}

/** One page of a contact's activity across all their channels, newest first. */
export async function fetchContactTimeline(visitorId: string, before?: number): Promise<TimelinePage> {
  const qs = before !== undefined ? `?before=${before}` : '';
  return apiFetch<TimelinePage>(`/api/visitors/${encodeURIComponent(visitorId)}/timeline${qs}`);
}

// ─── Merge / unlink (site admins) ───

/** Make two people one; the contact of `keepVisitorId` survives and gains the
 *  other's identities, chats and tickets. */
export async function mergeContacts(
  siteId: string,
  keepVisitorId: string,
  mergeVisitorId: string,
): Promise<{ contactId: string }> {
  return apiFetch(`/api/admin/sites/${encodeURIComponent(siteId)}/contacts/merge`, {
    method: 'POST',
    body: JSON.stringify({ keepVisitorId, mergeVisitorId }),
  });
}

/** Split one identity off into a contact of its own (undo a wrong link). */
export async function unlinkIdentity(siteId: string, visitorId: string): Promise<{ contactId: string }> {
  return apiFetch(`/api/admin/sites/${encodeURIComponent(siteId)}/contacts/unlink`, {
    method: 'POST',
    body: JSON.stringify({ visitorId }),
  });
}
